import * as React from "react";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getMyAdminStatus } from "@/lib/billing.functions";
import {
  listAdminEmails,
  getAdminEmailAggregates,
} from "@/lib/admin-emails.functions";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const PAGE_SIZE = 25;

export const Route = createFileRoute("/_authed/admin/emails")({
  head: () => ({
    meta: [
      { title: "Admin: Emails: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: AdminEmailsPage,
});

const TEMPLATE_OPTIONS = [
  "signup_welcome",
  "run_started",
  "run_completed",
  "run_failed",
  "irr_completed",
  "payment_receipt",
  "payment_failed",
  "credits_low",
  "credits_depleted",
  "checkout_abandoned",
  "inactivity_14d",
];

const STATUS_OPTIONS = [
  "queued",
  "sent",
  "failed",
  "bounced",
  "skipped_pref",
  "skipped_suppressed",
];

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function AdminEmailsPage() {
  const fetchList = useServerFn(listAdminEmails);
  const fetchAgg = useServerFn(getAdminEmailAggregates);

  const [template, setTemplate] = React.useState<string>("all");
  const [status, setStatus] = React.useState<string>("all");
  const [from, setFrom] = React.useState<string>("");
  const [to, setTo] = React.useState<string>("");
  const [page, setPage] = React.useState(1);

  React.useEffect(() => setPage(1), [template, status, from, to]);

  const args = {
    template: template === "all" ? null : template,
    status: status === "all" ? null : status,
    from: from ? new Date(from).toISOString() : null,
    to: to ? new Date(to).toISOString() : null,
  };

  const list = useQuery({
    queryKey: ["admin-emails", args, page],
    queryFn: () => fetchList({ data: { ...args, page, page_size: PAGE_SIZE } }),
  });
  const agg = useQuery({
    queryKey: ["admin-email-aggregates", args.from, args.to],
    queryFn: () => fetchAgg({ data: { from: args.from, to: args.to } }),
  });

  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <main className="mx-auto max-w-[1080px] px-4 pb-16 pt-6">
      <header className="mb-6">
        <Link
          to="/admin"
          className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
        >
          ← Back to admin
        </Link>
        <h1 className="mt-2 font-serif text-[28px] text-brand-ink">Emails.</h1>
        <p className="mt-1 text-[14px] text-brand-muted">
          Transactional email log. Source: <code>public.email_messages</code>.
        </p>
      </header>

      <section className="mb-6">
        <h2 className="mb-3 font-serif text-[18px] text-brand-ink">Per template</h2>
        {agg.isLoading ? (
          <p className="text-[14px] text-brand-muted">Loading…</p>
        ) : (agg.data?.length ?? 0) === 0 ? (
          <p className="text-[14px] text-brand-muted">No data in selected range.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {agg.data?.map((a) => (
              <div
                key={a.template}
                className="rounded-xl border border-brand-line bg-brand-paper p-3"
              >
                <div className="font-mono text-[12.5px] text-brand-ink">{a.template}</div>
                <div className="mt-2 grid grid-cols-2 gap-1 text-[12.5px] text-brand-muted">
                  <div>Total: <span className="text-brand-ink tabular-nums">{a.total}</span></div>
                  <div>Sent: <span className="text-brand-ink tabular-nums">{a.sent}</span></div>
                  <div>Delivery: <span className="text-brand-ink tabular-nums">{pct(a.delivery_rate)}</span></div>
                  <div>Bounced: <span className="text-brand-ink tabular-nums">{a.bounced}</span></div>
                  <div>Failed: <span className="text-brand-ink tabular-nums">{a.failed}</span></div>
                  <div>Skipped: <span className="text-brand-ink tabular-nums">{a.skipped}</span></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-brand-line bg-brand-paper p-3">
        <div className="min-w-[180px]">
          <label className="block text-[11.5px] text-brand-muted">Template</label>
          <Select value={template} onValueChange={setTemplate}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {TEMPLATE_OPTIONS.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <label className="block text-[11.5px] text-brand-muted">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[11.5px] text-brand-muted">From</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="block text-[11.5px] text-brand-muted">To</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </section>

      <section>
        {list.isLoading ? (
          <p className="text-[14px] text-brand-muted">Loading…</p>
        ) : (list.data?.rows.length ?? 0) === 0 ? (
          <p className="text-[14px] text-brand-muted">No email rows match these filters.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-brand-line bg-brand-paper">
            <table className="w-full text-[13px]">
              <thead className="bg-brand-paper-2 text-left text-[11.5px] uppercase tracking-wider text-brand-muted">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Template</th>
                  <th className="px-3 py-2">Recipient</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">HTTP</th>
                  <th className="px-3 py-2">Error</th>
                </tr>
              </thead>
              <tbody>
                {list.data?.rows.map((r) => (
                  <tr key={r.id} className="border-t border-brand-line align-top">
                    <td className="px-3 py-2 text-brand-muted whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11.5px]">{r.template}</td>
                    <td className="px-3 py-2">{r.recipient_email}</td>
                    <td className="px-3 py-2">{r.status}</td>
                    <td className="px-3 py-2 tabular-nums">{r.http_status ?? "—"}</td>
                    <td className="px-3 py-2 text-brand-muted text-[12px]">
                      {r.error ? r.error.slice(0, 120) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3 flex items-center justify-between text-[12.5px] text-brand-muted">
          <div>
            Page {page} of {totalPages} ({total} rows)
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}